import { z } from "zod"

import {
	asAttachmentId,
	asAuditLogEntryId,
	asCarrierId,
	asCorrectionRoundId,
	asCountryCode,
	asCountryVerticalId,
	asDocumentId,
	asDocumentTypeId,
	asHsCode,
	asLaneId,
	asLotId,
	asMerchantChangeId,
	asNotificationId,
	asPartyId,
	asPortId,
	asShipmentId,
	asUnlocode,
	asVerticalId,
	asWarehouseId,
} from "../ids.js"

/**
 * Branded-ID + nominal-code zod schemas for @app/shared.
 *
 * Centralized here so entity schemas can import any IdSchema they need
 * without forming circular imports across entity files. Each schema
 * validates the raw string then `.transform()`-es it via the matching
 * `as*` constructor from `../ids`, so a `.parse()` output is already
 * nominally typed.
 *
 * Auth-flow ID schemas (`UserIdSchema`, `RoleIdSchema`, `TenantIdSchema`)
 * still live in `./auth` for now; consolidation planned for a follow-up
 * PR.
 */

const Bounded = (max = 256) => z.string().min(1).max(max)

export const ShipmentIdSchema = Bounded().transform(asShipmentId)
export const LotIdSchema = Bounded().transform(asLotId)
export const DocumentIdSchema = Bounded().transform(asDocumentId)
export const DocumentTypeIdSchema = Bounded().transform(asDocumentTypeId)
export const PartyIdSchema = Bounded().transform(asPartyId)
export const CarrierIdSchema = Bounded().transform(asCarrierId)
export const LaneIdSchema = Bounded().transform(asLaneId)
export const PortIdSchema = Bounded().transform(asPortId)
export const VerticalIdSchema = Bounded().transform(asVerticalId)
export const CountryVerticalIdSchema = Bounded().transform(asCountryVerticalId)
export const WarehouseIdSchema = Bounded().transform(asWarehouseId)
export const CorrectionRoundIdSchema = Bounded().transform(asCorrectionRoundId)
export const MerchantChangeIdSchema = Bounded().transform(asMerchantChangeId)
export const NotificationIdSchema = Bounded().transform(asNotificationId)
export const AttachmentIdSchema = Bounded().transform(asAttachmentId)
export const AuditLogEntryIdSchema = Bounded().transform(asAuditLogEntryId)

// --- Nominal codes ----------------------------------------------------
// ISO 3166-1 alpha-2 (2 chars), UN/LOCODE (5 chars), HS code (6-10
// digits). Format-validated here so consumers can trust a `.parse()`
// output is well-formed without a second check.

export const CountryCodeSchema = z
	.string()
	.length(2)
	.regex(/^[A-Z]{2}$/, "ISO 3166-1 alpha-2 (e.g. NG, BJ, AE) expected")
	.transform(asCountryCode)

export const UnlocodeSchema = z
	.string()
	.length(5)
	.regex(/^[A-Z]{5}$/, "UN/LOCODE: 2-letter country + 3-letter location")
	.transform(asUnlocode)

export const HsCodeSchema = z
	.string()
	.regex(/^\d{6,10}$/, "HS code: 6-10 digits")
	.transform(asHsCode)
